import { onBeforeUnmount, ref, watch } from 'vue';
import { LAST_ACTIVITY_KEY, useAuthStore } from '../stores/authStore';

/**
 * Inactivity auto-logout for real SSO users.
 *
 * Real users are signed out after IDLE_LIMIT_MS of no interaction; a
 * warning surfaces WARNING_LEAD_MS before the cutoff so they can keep the
 * session alive. Dev-bypass sessions are exempt — there is no real session
 * to end, and /auth/login would just re-mint the sentinel token, so we
 * never even attach the listeners for them (mirrors the disabled "Sign
 * out" affordance in UserMenu).
 *
 * The clock is the LAST_ACTIVITY_KEY timestamp in localStorage (owned by
 * authStore — see there). Because it's localStorage and we re-read it every
 * tick rather than trusting an in-memory value:
 *   - activity in any tab keeps every tab's session alive, and
 *   - a reload doesn't reset the clock (authStore only rewrites it on a
 *     fresh sign-in), so a reload can't be used to dodge the timeout.
 *
 * Meant to be called once from the always-mounted App.vue shell. Returns
 * the reactive state + actions wired to <IdleLogoutWarning>.
 */

/** Idle this long and a real user's session ends. */
const IDLE_LIMIT_MS = 30 * 60 * 1000;
/** Show the warning this far ahead of the cutoff. */
const WARNING_LEAD_MS = 60 * 1000;
/** How often we re-evaluate idle time against the shared clock. */
const TICK_MS = 1000;
/** Coalesce activity writes so a mousemove storm doesn't thrash storage. */
const ACTIVITY_WRITE_THROTTLE_MS = 5000;

// Passive signals of "the user is still here". scroll/mousemove are noisy
// but cheap thanks to the throttle above; keydown/click/touchstart cover
// deliberate interaction.
const ACTIVITY_EVENTS = [
    'mousemove',
    'mousedown',
    'click',
    'keydown',
    'scroll',
    'touchstart',
] as const;

export function useIdleLogout() {
    const authStore = useAuthStore();

    const warningVisible = ref(false);
    const secondsRemaining = ref(0);

    let tickHandle: number | null = null;
    let lastWrite = 0;
    let tracking = false;

    function readLastActivity(): number {
        try {
            const raw = localStorage.getItem(LAST_ACTIVITY_KEY);
            const parsed = raw === null ? NaN : Number(raw);
            return Number.isFinite(parsed) ? parsed : NaN;
        } catch {
            return NaN;
        }
    }

    function writeLastActivity(ts: number): void {
        try {
            localStorage.setItem(LAST_ACTIVITY_KEY, String(ts));
        } catch {
            // Best-effort — see authStore.setToken.
        }
    }

    // Throttled activity recorder. While the warning is showing we ignore
    // passive activity on purpose: dismissing it requires an explicit
    // "Stay signed in" so an incidental mousemove over the dialog can't
    // silently extend the session. (Activity in *another* tab still keeps
    // this one alive — tick() re-reads the shared clock.)
    function onActivity(): void {
        if (warningVisible.value) return;
        const t = Date.now();
        if (t - lastWrite < ACTIVITY_WRITE_THROTTLE_MS) return;
        lastWrite = t;
        writeLastActivity(t);
    }

    function stayActive(): void {
        const t = Date.now();
        lastWrite = t;
        writeLastActivity(t);
        warningVisible.value = false;
    }

    function logoutNow(): void {
        stopTracking();
        authStore.logout();
    }

    function tick(): void {
        let last = readLastActivity();
        if (!Number.isFinite(last)) {
            // No timestamp yet (first session, or storage cleared mid-flight).
            last = Date.now();
            writeLastActivity(last);
        }
        const msUntilCutoff = IDLE_LIMIT_MS - (Date.now() - last);
        if (msUntilCutoff <= 0) {
            logoutNow();
            return;
        }
        if (msUntilCutoff <= WARNING_LEAD_MS) {
            warningVisible.value = true;
            secondsRemaining.value = Math.ceil(msUntilCutoff / 1000);
        } else {
            warningVisible.value = false;
        }
    }

    function startTracking(): void {
        if (tracking) return;
        tracking = true;
        // Seed a timestamp if none exists so a brand-new session has a clock
        // immediately (authStore.setToken normally beats us to this).
        if (!Number.isFinite(readLastActivity())) writeLastActivity(Date.now());
        for (const ev of ACTIVITY_EVENTS) {
            window.addEventListener(ev, onActivity, { passive: true });
        }
        tickHandle = window.setInterval(tick, TICK_MS);
        // Evaluate right away: covers reloading into an already-expired
        // session, which should sign out without waiting a full tick.
        tick();
    }

    function stopTracking(): void {
        if (!tracking) return;
        tracking = false;
        for (const ev of ACTIVITY_EVENTS) {
            window.removeEventListener(ev, onActivity);
        }
        if (tickHandle !== null) {
            window.clearInterval(tickHandle);
            tickHandle = null;
        }
        warningVisible.value = false;
    }

    // Track only while a real user is signed in. The watcher flips tracking
    // on at login and off the moment the token clears or turns out to be the
    // dev-bypass sentinel.
    watch(
        () => authStore.isAuthenticated && !authStore.isDevBypass,
        (isRealUser) => {
            if (isRealUser) startTracking();
            else stopTracking();
        },
        { immediate: true },
    );

    onBeforeUnmount(stopTracking);

    return { warningVisible, secondsRemaining, stayActive, logoutNow };
}
